// src/App.tsx
import React, { useEffect, useRef, useState } from 'react';
import Navbar from 'react-bootstrap/Navbar';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Button, Offcanvas } from 'react-bootstrap';
import './App.css';
import Forbidden from './views/Forbidden';
import EventNotFound from './views/EventNotFound';
import Main from './views/Main';
import PackagesMembership from './views/PackagesMembership';
import PrivateClasses from './views/PrivateClasses';
import RootStore, { RootStoreContext } from './stores/RootStore';
import { ErrorCode, RETURN_TO_RENTAL_TAB_KEY } from './stores/RegistrationStore';
import TerminalHeader from './components/TerminalHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; 
import { faGear } from '@fortawesome/free-solid-svg-icons'; 
import { isTokenExpired } from './util';
import AdminLogin from './views/AdminLogin';
import { StepName } from './config/Wizard';
import { consumePendingEventPath, parseEventId, saveCurrentEventPath, savePendingEventPath } from './stores/EventDeepLinkSession';
import { handleWhatsNewBack } from './campaignNavigation';

const EVENT_ROUTE_WORKFLOW_STEPS = [
  StepName.REGISTRATION_SELECT_TRAINING,
  StepName.REGISTRATION_SUCCESS,
  StepName.REGISTRATION_FAILED,
  StepName.REGISTRATION_PARTIAL_SUCCESS
];

const ACCOUNT_BLOCKING_ERRORS = [
  ErrorCode.NO_PACKAGE,
  ErrorCode.PENDING_EARLY_BIRD,
  ErrorCode.DEBT_FOUND
];

type EventRouteStepAction = 'wait' | 'ready' | 'select';

const getEventRouteStepAction = (
  step: StepName,
  errorCode: ErrorCode | undefined,
  initialized: boolean
): EventRouteStepAction => {
  if (step === StepName.REGISTRATION_AUTHENTICATE || step === StepName.REGISTRATION_AUTHENTICATE_OTP) {
    return 'wait';
  }

  if (initialized) {
    return EVENT_ROUTE_WORKFLOW_STEPS.includes(step) ? 'ready' : 'select';
  }

  const accountBlocked = step === StepName.REGISTRATION_FAILED && ACCOUNT_BLOCKING_ERRORS.includes(errorCode);
  return step === StepName.REGISTRATION_SELECT_TRAINING || accountBlocked ? 'ready' : 'select';
};

const PackagesMembershipGate = observer(() => {
  const store = React.useContext(RootStoreContext);

  useEffect(() => {
    if (store.authStore.isUserFlow) {
      store.registrationStore.ensureUserLoaded();
      store.studioConfigStore.fetchStudioConfigs();
    }
  }, [store]);

  if (!store.authStore.isUserFlow) {
    return store.authStore.isAuthenticated ? <Navigate to="/403" /> : <Navigate to="/" />;
  }

  if (!store.registrationStore.user?.id || !store.studioConfigStore.studioConfigs) {
    return null;
  }

  return <PackagesMembership />;
});

export const PrivateClassesGate = observer(() => {
  const store = React.useContext(RootStoreContext);

  useEffect(() => {
    if (store.authStore.isUserFlow) {
      store.registrationStore.ensureUserLoaded();
      store.studioConfigStore.fetchStudioConfigs();
    }
  }, [store]);

  if (!store.authStore.isUserFlow) {
    return store.authStore.isAuthenticated ? <Navigate to="/403" /> : <Navigate to="/" />;
  }

  if (!store.registrationStore.user?.id || !store.studioConfigStore.studioConfigs) {
    return null;
  }

  return store.studioConfigStore.isPrivateClassesAllowed(store.registrationStore.user.id)
    ? <PrivateClasses />
    : <Navigate to="/403" />;
});

export const EventRoute = observer(() => {
  const store = React.useContext(RootStoreContext);
  const { eventId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const parsedEventId = parseEventId(eventId);
  const initializedEventId = useRef<number | null>(null);

  useEffect(() => {
    if (parsedEventId === null) {
      return;
    }

    if (!store.authStore.isAuthenticated) {
      savePendingEventPath(location.pathname);
      navigate('/', { replace: true });
      return;
    }

    if (!store.authStore.isUserFlow && !store.authStore.isAdminFlow) {
      savePendingEventPath(location.pathname);
      store.authStore.logout();
      navigate('/', { replace: true });
    }
  }, [
    location.pathname,
    navigate,
    parsedEventId,
    store.authStore,
    store.authStore.isAdminFlow,
    store.authStore.isAuthenticated,
    store.authStore.isUserFlow
  ]);

  useEffect(() => {
    if (parsedEventId === null || !store.registrationStore.user?.id) {
      return;
    }

    const currentStep = store.wizardStore.currentStepName;
    const initialized = initializedEventId.current === parsedEventId;
    const action = getEventRouteStepAction(
      currentStep,
      store.registrationStore.error?.code,
      initialized
    );
    if (action === 'wait') {
      return;
    }

    if (!initialized) {
      initializedEventId.current = parsedEventId;
    }
    if (action === 'select') {
      if (currentStep === StepName.REGISTRATION_FAILED && store.registrationStore.error) {
        store.registrationStore.clearError();
      }
      store.wizardStore.setStep(StepName.REGISTRATION_SELECT_TRAINING);
    }
  }, [parsedEventId, store.registrationStore.error?.code, store.registrationStore.user?.id, store.wizardStore, store.wizardStore.currentStepName]);

  if (parsedEventId === null) {
    return <EventNotFound />;
  }

  if (!store.authStore.isAuthenticated) {
    return null;
  }

  if (store.authStore.isAdminFlow) {
    return <Navigate to="/403" replace />;
  }

  if (!store.authStore.isUserFlow) {
    return null;
  }

  if (store.registrationStore.user?.id) {
    const action = getEventRouteStepAction(
      store.wizardStore.currentStepName,
      store.registrationStore.error?.code,
      initializedEventId.current === parsedEventId
    );
    if (action !== 'ready') {
      return null;
    }
  }

  return <Main />;
});

const AppContent = observer(() => {
  const store = React.useContext(RootStoreContext);
  const [showDrawer, setShowDrawer] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname !== '/') {
      return;
    }

    // Handle browser back button
    window.history.pushState(null, '', window.location.href);
    window.onpopstate = () => {
      window.history.pushState(null, '', window.location.href);

      if (handleWhatsNewBack(store)) {
        return;
      }
      
      // If viewing event details, go back to event list
      if (store.registrationStore.selectedEventId !== null) {
        store.registrationStore.selectedEventId = null;
        return;
      }

      if (store.registrationStore.isAuthenticationStep) {
        return;
      }

      if (typeof sessionStorage !== 'undefined') {
        const hasRentalTab = store.studioConfigStore.rentEnabled;
        const rentalTabIndex = store.registrationStore.sellingItemCategories.length;
        const isRentalActive = hasRentalTab && store.registrationStore.activeCategory === rentalTabIndex;

        if (isRentalActive) {
          sessionStorage.setItem(RETURN_TO_RENTAL_TAB_KEY, 'true');
        } else {
          sessionStorage.removeItem(RETURN_TO_RENTAL_TAB_KEY);
        }
      }
      
      // Otherwise, restart and go to home (default behavior)
      store.registrationStore.restart();
      window.location.href = '/';
    };

    return () => {
      window.onpopstate = null;
    };
  }, [location.pathname, store]);

  useEffect(() => {
    // Check authentication
    store.authStore.checkAuth();
    if (store.authStore.isAuthenticated) {
      const token = localStorage.getItem('jwtToken');
      if (token && isTokenExpired(token)) {
        saveCurrentEventPath();
        const isAuthenticationStep = store.registrationStore.isAuthenticationStep;
        store.authStore.logout(!isAuthenticationStep);
        if (!isAuthenticationStep) {
          store.registrationStore.restart();
        }
        navigate('/', { replace: true });
      }
    }
  }, [location.pathname, navigate, store.authStore, store.registrationStore]);

  useEffect(() => {
    const currentStep = store.wizardStore.currentStepName;
    const loginComplete = currentStep !== StepName.REGISTRATION_AUTHENTICATE &&
      currentStep !== StepName.REGISTRATION_AUTHENTICATE_OTP;

    if (location.pathname === '/' &&
        store.authStore.isUserFlow &&
        store.registrationStore.user?.id &&
        loginComplete) {
      const pendingEventPath = consumePendingEventPath();
      if (pendingEventPath) {
        navigate(pendingEventPath, { replace: true });
      }
    }
  }, [location.pathname, navigate, store.authStore.isUserFlow, store.registrationStore.user?.id, store.wizardStore.currentStepName]);

  useEffect(() => {
    // Update title with studio name
    const updateTitle = async () => {
      try {
        await store.studioConfigStore.fetchStudioConfigs();
        document.title = `Checkin App - ${store.studioConfigStore.studioName}`;
      } catch (error) {
        console.error('Failed to fetch studio config for title:', error);
      }
    };
    updateTitle();
  }, [store.studioConfigStore]);

  const shouldHideNavbar = store.wizardStore.currentStepName === StepName.REGISTRATION_AUTHENTICATE || store.wizardStore.currentStepName === StepName.REGISTRATION_AUTHENTICATE_OTP;

  return (
    <>
      {!shouldHideNavbar && (
        <Navbar bg="light" className="nav-bar-container">
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <TerminalHeader />
        </Navbar>
      )}

      {store.authStore.isAdminFlow && (
        <div className="position-fixed bottom-0 end-0 m-3">
          <Button 
            variant= "secondary"
            onClick={() => setShowDrawer(true)}
            className="rounded-circle"
          >
            <FontAwesomeIcon icon={faGear} /> 
          </Button>
        </div>
      )}

      {store.authStore.isAdminFlow && (
      <Offcanvas
        show={showDrawer}
        onHide={() => setShowDrawer(false)}
        placement="end"
        className="h-auto"
        style={{ 
          position: 'fixed',
          bottom: '1rem',
          right: '1rem',
          top: 'auto',
          width: '200px',
          borderRadius: '10px'
        }}
      >
        <Offcanvas.Body className="d-flex flex-column">
          <Button 
            variant="danger" 
            onClick={() => {
              store.registrationStore.restart();
              store.authStore.logout();
              setShowDrawer(false);
            }}
            className="mt-2"
          >
            Logout (admin only)
          </Button>
        </Offcanvas.Body>
      </Offcanvas>
      )}

      <Routes>
        <Route path="/admin" element={ store.authStore.isAuthenticated ? <Navigate to="/" /> : <AdminLogin /> } />
        <Route path="/403" element={<Forbidden />} />
        <Route
          path="/packages-membership"
          element={<PackagesMembershipGate />}
        />
        <Route
          path="/private-classes"
          element={<PrivateClassesGate />}
        />
        <Route path="/event/:eventId" element={<EventRoute />} />
        <Route path="/" element={ <Main /> }
        />
      </Routes>
    </>
  );
});

const App = () => {
  return (
    <RootStoreContext.Provider value={new RootStore()}>
      <Router basename={process.env.PUBLIC_URL}>
        <AppContent />
      </Router>
    </RootStoreContext.Provider>
  );
};

export default App;
